import React, { useEffect, useMemo, useState } from "react";
import { normalize } from "common/lib/normalize";
import { getLTAServices } from "common/services/ltas.service";
import { getAgencies } from "common/services/agencies.service";
import { FilterSelect } from "Components/Common/TableFilters";
import { LTA_STATUSES } from "./ltaOptions";

/**
 * Filter dropdowns for the LTA tables. Each holds a plain `{ value, label }`
 * object so the table's URL round-trip can serialise it, and each is clearable
 * so a single filter can be dropped without resetting the rest.
 */

type FilterProps = {
  value: any;
  onChange: (value: any) => void;
};

/** Loads `{ value, label }` options once from a lookup endpoint. */
const useLookupOptions = (fetcher: (params: any) => Promise<any>) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response: any = await fetcher({
          pagination: { page: 1, pageSize: 10000 },
          sort: ["name"],
        });
        setData(normalize(response.data));
      } catch (e) {
        // no-op
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetcher]);

  const options = useMemo(
    () => data.map((el: any) => ({ value: el?.id, label: el?.name })),
    [data]
  );

  return { options, loading };
};

export const LTAServiceFilter = ({ value, onChange }: FilterProps) => {
  const { options, loading } = useLookupOptions(getLTAServices);

  return (
    <FilterSelect
      id="ltaServiceFilter"
      placeholder="Filter by service"
      value={value}
      onChange={onChange}
      options={options}
      isLoading={loading}
    />
  );
};

export const LTAAgencyFilter = ({ value, onChange }: FilterProps) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response: any = await getAgencies({
          pagination: { page: 1, pageSize: 10000 },
          filters: { category: { $null: false } },
          sort: ["name"],
        });
        setData(normalize(response.data));
      } catch (e) {
        // no-op
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const options = useMemo(
    () => data.map((el: any) => ({ value: el?.id, label: el?.name })),
    [data]
  );

  return (
    <FilterSelect
      id="ltaAgencyFilter"
      placeholder="Filter by agency"
      value={value}
      onChange={onChange}
      options={options}
      isLoading={loading}
    />
  );
};

export const LTAStatusFilter = ({ value, onChange }: FilterProps) => {
  // Statuses are derived from the term dates; the list pages turn the choice
  // into the matching date predicates.
  const options = useMemo(
    () => LTA_STATUSES.map(({ value, label }) => ({ value, label })),
    []
  );

  return (
    <FilterSelect
      id="ltaStatusFilter"
      placeholder="Filter by status"
      value={value}
      onChange={onChange}
      options={options}
    />
  );
};
